import _get from 'lodash/get';
import _every from 'lodash/every';
import _flatten from 'lodash/flatten';
import _uniqBy from 'lodash/uniqBy';
import {
  CONTENT_TYPE_CATEGORIES,
  FILE_DISPLAY_NAME_MAX_LENGTH,
  VALID_CONTENT_TYPES,
} from './constants';
import { pushHyperlinkClickInteraction } from './gaTags';
import { CN_HOST_ENVS } from '../../utils/constants';

export const getTroubleshootingGuideName = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('troubleshootingGuide') || '';
};

export const validateConfig = config => !!(_get(config, 'data.screens') || []).length;

const meetsConditions = (formInputValues, conditions) =>
  _every(conditions, ({ field, values }) => values.includes(formInputValues[field] || null));

export const shouldRender = formInputValues => item => {
  if (!item) return false;
  if (!item.renderConditions) return true;

  if (Array.isArray(item.renderConditions[0])) {
    return item.renderConditions.some(conditions => meetsConditions(formInputValues, conditions));
  }

  return meetsConditions(formInputValues, item.renderConditions);
};

export const getNextScreenIndex = ({ screens = [], currentScreenIndex = null, formInputValues }) =>
  screens.findIndex(
    (screen, i) =>
      (currentScreenIndex === null || i > currentScreenIndex) && // is upcoming screen
      shouldRender(formInputValues)(screen)
  );

export const validateForm = (currentScreenContents = []) =>
  _every(currentScreenContents, ({ type, required, value, validationRegex }) => {
    if (!required || !VALID_CONTENT_TYPES[type]) {
      return true;
    }

    if (!validationRegex) {
      return !!value;
    }

    const regex = new RegExp(validationRegex);
    return !!value && regex.test(value);
  });

const extractSupportNoteFromInputField = (formInputValues, contentKey, inputConfig) => {
  const { type, label, supportLabel, data, hiddenFromSupport } = inputConfig || {};
  if (hiddenFromSupport || type === VALID_CONTENT_TYPES.fileUpload) return '';

  const detailKey = supportLabel || label || '';

  const value = formInputValues[contentKey];
  const renderedOptions = (data || []).filter(shouldRender(formInputValues));
  const { label: optionLabel, supportLabel: optionSupportLabel } =
    renderedOptions.find(({ value: optionValue }) => optionValue === value) || {};
  const detailValue = optionSupportLabel || optionLabel || value || '';

  if (!detailKey || !detailValue) return '';
  return `${detailKey}: ${detailValue}`;
};

const extractSupportNoteFromTextContentField = textContentConfig => {
  const { supportValue, supportLabel } = textContentConfig || {};
  if (!supportValue || !supportLabel) return '';
  return `${supportLabel}: ${supportValue}`;
};

const getCompletedTroubleshootingSteps = (screens, formInputValues, troubleshootingStepsText) => {
  const renderedScreens = screens.filter(screen => shouldRender(formInputValues)(screen));

  const listOfStepsCompleted = renderedScreens.reduce((steps, screen) => {
    if (screen.troubleshootingStepsLabel) {
      steps.push(screen.troubleshootingStepsLabel);
    }

    return steps;
  }, []);

  return listOfStepsCompleted.length > 0
    ? `${troubleshootingStepsText}: ${listOfStepsCompleted.join(', ')}`
    : '';
};

export const getSupportNote = (config, formInputValues, troubleshootingStepsText) => {
  const allScreens = _get(config, 'screens') || [];
  const orderedFields = _uniqBy(
    _flatten(allScreens.map(screen => _get(screen, 'contents') || [])),
    'key'
  );
  const inputConfigs = _get(config, CONTENT_TYPE_CATEGORIES.INPUT) || {};
  const textContentConfigs = _get(config, CONTENT_TYPE_CATEGORIES.TEXT_CONTENT) || {};
  const supportNotes = orderedFields
    .filter(
      ({ key, type }) =>
        (type === CONTENT_TYPE_CATEGORIES.INPUT &&
          shouldRender(formInputValues)(inputConfigs[key])) ||
        (type === CONTENT_TYPE_CATEGORIES.TEXT_CONTENT &&
          shouldRender(formInputValues)(textContentConfigs[key]))
    )
    .map(({ key, type }) => {
      if (type === CONTENT_TYPE_CATEGORIES.INPUT) {
        return extractSupportNoteFromInputField(formInputValues, key, inputConfigs[key]);
      }
      if (type === CONTENT_TYPE_CATEGORIES.TEXT_CONTENT) {
        return extractSupportNoteFromTextContentField(textContentConfigs[key]);
      }
      return '';
    });

  const completedTroubleshootingSteps = getCompletedTroubleshootingSteps(
    allScreens,
    formInputValues,
    troubleshootingStepsText
  );

  const noteRows = [_get(config, 'name'), completedTroubleshootingSteps, ...supportNotes];
  return noteRows.filter(Boolean);
};

const getNextButtonRedirectDetails = (config, formInputValues) => {
  const buttons = _get(config, 'buttons');
  const redirectDetails = Object.values(buttons ?? {}).find(({ type, renderConditions }) => {
    return (
      type === 'nextButton' &&
      renderConditions.some(conditions => meetsConditions(formInputValues, conditions))
    );
  });

  return redirectDetails ?? {};
};

export const getPrimaryCTADetails = (config, screen, formInputValues) => {
  const buttons = _get(screen, 'primaryCTA');
  if (!buttons) {
    return getNextButtonRedirectDetails(config, formInputValues);
  }

  const primaryCTAWithRenderConditions = buttons.find(({ renderConditions }) => {
    return (renderConditions ?? []).some(conditions =>
      meetsConditions(formInputValues, conditions)
    );
  });
  const primaryCTANoRenderConditions = buttons.find(({ renderConditions }) => {
    return !renderConditions;
  });

  const primaryCTA = primaryCTAWithRenderConditions ?? primaryCTANoRenderConditions;
  if (!primaryCTA) {
    return {};
  }

  return primaryCTA;
};

export const configureLinks = links => {
  if (!links) {
    return;
  }
  const params = new URLSearchParams(document.location.search);
  const troubleshootingGuide = params.get('troubleshootingGuide');

  Object.keys(links).forEach(linkName => {
    const linkDetails = links[linkName];
    linkDetails.onClick = () =>
      pushHyperlinkClickInteraction(troubleshootingGuide, linkDetails.value);
  });
};

export const getComparisonDate = numDays => {
  const comparisonDate = new Date();
  comparisonDate.setDate(comparisonDate.getDate() + numDays);

  return comparisonDate;
};

export const isFileTypeValid = ({ acceptType, file }) => {
  if (acceptType === '*') return true;
  const validFileTypes = acceptType.split(', ');
  return validFileTypes.includes(file.type);
};

export const getFileDisplayName = fileName => {
  return fileName.length > FILE_DISPLAY_NAME_MAX_LENGTH
    ? `...${fileName.substring(fileName.length - FILE_DISPLAY_NAME_MAX_LENGTH)}`
    : fileName;
};

export const handleSelectedIssueOverrides = (
  config,
  formInputValues,
  setTroubleshootingSupportOverrides
) => {
  const overrides = config?.conditionalUpdates?.serviceCategoryOverrides ?? [];
  if (overrides.length === 0) {
    return;
  }

  for (let i = 0; i < overrides.length; i += 1) {
    const override = overrides[i];
    const shouldUpdate = shouldRender(formInputValues)(override);

    const shouldSetOverrides =
      shouldUpdate === true && (!!override.newSymptomCode || !!override.chatTeam);

    if (shouldSetOverrides) {
      setTroubleshootingSupportOverrides({
        chatTeam: override.chatTeam ?? null,
        symptomCode: override.newSymptomCode ?? null,
      });
      return;
    }
  }

  setTroubleshootingSupportOverrides({ chatTeam: null, symptomCode: null });
};

export const getHostEnv = (isApp, isWechat) => {
  if (isApp) {
    return CN_HOST_ENVS.APP;
  }
  if (isWechat) {
    return CN_HOST_ENVS.WECHAT;
  }
  return CN_HOST_ENVS.WEB;
};
